import { AxiosError } from 'axios';
import useSWRMutation from 'swr/mutation';

import {
  ApplicationDecisionT,
  consentsList,
  ConsentTypeT,
  DELAWARE_CONSENT,
  IovationBlackboxAtomT,
  IovationPurchasedAtomT,
  isDefined,
  LoanPurposeValueT,
  SwrKeyT,
  WithStatusT,
} from '@consumer-frontend/nc-utils';

import { usePurchaseIovation } from './usePurchaseIovation';
import {
  apiEndpoints,
  applyFetchers,
} from '../lib';

export type UseSubmitApplicationPropsT = {
  consentsRequired?: boolean;
  isConsentBundleChecked?: boolean;
  isDelawareConsentChecked?: boolean;
  isReturningCustomerFlow?: boolean;
  loanAmount?: number;
  loanPurpose?: LoanPurposeValueT;
  password?: string;
};

export type SubmitApplicationRequestT = {
  changed_by: 'loan_application_organic';
  consents: ConsentTypeT[];
  sdr_consent: boolean;
  sms_consent: boolean;
  password?: string;
  requested_loan_amount?: string;
  stated_loan_purpose?: LoanPurposeValueT;
};
export type SubmitApplicationResponseT = {
  decision: ApplicationDecisionT;
};

export type UseSubmitApplicationResultT = {
  isIovationPurchaseSuccessful: boolean;
  response?: WithStatusT<SubmitApplicationResponseT>;
};

export const useSubmitApplication = (
  {
    consentsRequired = true,
    isConsentBundleChecked = false,
    isDelawareConsentChecked,
    isReturningCustomerFlow,
    loanAmount,
    loanPurpose,
    password,
  }: UseSubmitApplicationPropsT,
  iovationBlackboxAtom: IovationBlackboxAtomT,
  iovationPurchasedAtom: IovationPurchasedAtomT,
) => {
  // eslint-disable-next-line no-nested-ternary
  const consentsChecked = isConsentBundleChecked
    ? isDelawareConsentChecked
      ? [...consentsList, DELAWARE_CONSENT]
      : consentsList
    : []
  ;

  const isReadyToSubmit = (isReturningCustomerFlow || isDefined(password))
    && (!consentsRequired || isConsentBundleChecked);

  const submitApplicationData = (isReadyToSubmit
    ? {
      changed_by: 'loan_application_organic',
      consents: consentsChecked,
      sdr_consent: isConsentBundleChecked,
      sms_consent: isConsentBundleChecked,
      ...(!isReturningCustomerFlow && { password }),
      ...(isDefined(loanAmount) && { requested_loan_amount: loanAmount.toString() }),
      ...(isDefined(loanPurpose) && { stated_loan_purpose: loanPurpose }),
    }
    : {}
  ) as SubmitApplicationRequestT;

  const {
    isMutating: isPurchasingIovation,
    purchaseIovation,
  } = usePurchaseIovation(iovationBlackboxAtom, iovationPurchasedAtom);

  const {
    data: submissionResponse,
    error,
    isMutating: isSubmitting,
    trigger,
  } = useSWRMutation<
    WithStatusT<SubmitApplicationResponseT>,
    AxiosError,
    SwrKeyT
  >(
    () => (isReadyToSubmit ? apiEndpoints.apply.submitApplication : null),
    (url: string) => applyFetchers
      .post<SubmitApplicationRequestT, SubmitApplicationResponseT>(url, { arg: submitApplicationData })
    ,
  );

  const submitApplication = async (): Promise<UseSubmitApplicationResultT> => {
    const isIovationPurchaseSuccessful = await purchaseIovation();

    if (!isIovationPurchaseSuccessful) {
      return { isIovationPurchaseSuccessful, response: undefined };
    }

    const response = await trigger();

    return { isIovationPurchaseSuccessful, response };
  };

  return {
    error,
    isMutating: isSubmitting || isPurchasingIovation,
    submissionResponse,
    submitApplication,
  };
};
